import React, {useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useQuery} from 'react-query'
import ApexCharts, {ApexOptions} from 'apexcharts'
import dataAxios from '../../modules/auth/core/dataAxios'

// ── Azure Curator color tokens ────────────────────────────────────────────
const AZ = {
  primary: '#00347f',
  primaryContainer: '#1a4ba1',
  secondary: '#006876',
  tertiaryFixed: '#ffb870',
  surfaceLow: '#f2f4f6',
  surfaceContainer: '#eceef0',
  surfaceHigh: '#e6e8ea',
  onSurface: '#191c1e',
  onSurfaceVariant: '#434652',
  outline: '#737783',
  errorContainer: '#ffdad6',
  onErrorContainer: '#93000a',
  secondaryFixed: '#a1efff',
  onSecondaryContainer: '#006573',
}

const fetchFinancial = async (from: string, to: string) => {
  const res = await dataAxios.get('/reports/financial', {params: {from, to}})
  return res.data?.data
}

const fetchPromotions = async (from: string, to: string) => {
  const res = await dataAxios.get('/reports/promotions', {params: {from, to}})
  return res.data?.data
}

const defaultDateFromOneYearAgo = () => {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 1)
  return d.toISOString().slice(0, 10)
}

// ── Sub-components ────────────────────────────────────────────────────────

type KpiCardProps = {
  icon: string
  iconBg: string
  iconColor: string
  label: string
  value: number | string
  sub?: string
}
const KpiCard: React.FC<KpiCardProps> = ({icon, iconBg, iconColor, label, value, sub}) => (
  <div
    className='reports-kpi-card'
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '1.25rem',
      padding: '1.5rem',
      boxShadow: '0 4px 24px -4px rgba(0,52,127,0.07)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
    }}
  >
    <div
      style={{
        width: '2.75rem',
        height: '2.75rem',
        borderRadius: '0.75rem',
        backgroundColor: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <span
        className='material-symbols-outlined'
        style={{fontSize: '22px', color: iconColor, fontVariationSettings: "'FILL' 1"}}
      >
        {icon}
      </span>
    </div>
    <div style={{minWidth: 0}}>
      <p
        style={{
          margin: 0,
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: AZ.onSurfaceVariant,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: '0.25rem 0 0',
          fontSize: '1.75rem',
          fontWeight: 800,
          color: AZ.onSurface,
          fontFamily: 'Manrope, sans-serif',
          lineHeight: 1.1,
        }}
      >
        {value}
      </p>
      {sub && (
        <p style={{margin: '0.25rem 0 0', fontSize: '0.75rem', color: AZ.outline}}>{sub}</p>
      )}
    </div>
  </div>
)

type SectionLabelProps = {children: React.ReactNode}
const SectionLabel: React.FC<SectionLabelProps> = ({children}) => (
  <p
    style={{
      fontSize: '0.6875rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      color: AZ.onSurfaceVariant,
      margin: '0 0 1rem',
    }}
  >
    {children}
  </p>
)

type ChartCardProps = {title: string; children: React.ReactNode}
const ChartCard: React.FC<ChartCardProps> = ({title, children}) => (
  <div
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '1.25rem',
      boxShadow: '0 4px 24px -4px rgba(0,52,127,0.07)',
      overflow: 'hidden',
    }}
  >
    <div style={{padding: '1.5rem 1.5rem 0'}}>
      <p
        style={{
          margin: 0,
          fontFamily: 'Manrope, sans-serif',
          fontSize: '1rem',
          fontWeight: 700,
          color: AZ.onSurface,
        }}
      >
        {title}
      </p>
    </div>
    <div style={{padding: '1rem 1.5rem 1.5rem'}}>{children}</div>
  </div>
)

// ── Main Page ─────────────────────────────────────────────────────────────

const ReportsPage = () => {
  const intl = useIntl()
  const today = new Date().toISOString().slice(0, 10)
  const [from, setFrom] = React.useState(defaultDateFromOneYearAgo)
  const [to, setTo] = React.useState(today)

  const {data, isLoading, isFetching, refetch} = useQuery(
    ['reports-financial', from, to],
    () => fetchFinancial(from, to),
    {enabled: !!from && !!to, keepPreviousData: true},
  )

  const {data: promoData} = useQuery(
    ['reports-promotions', from, to],
    () => fetchPromotions(from, to),
    {enabled: !!from && !!to, keepPreviousData: true},
  )

  const revenueRef = useRef<HTMLDivElement | null>(null)
  const dailyRef = useRef<HTMLDivElement | null>(null)
  const cityRef = useRef<HTMLDivElement | null>(null)
  const topToursRef = useRef<HTMLDivElement | null>(null)
  const chartRevenue = useRef<ApexCharts | null>(null)
  const chartDaily = useRef<ApexCharts | null>(null)
  const chartCity = useRef<ApexCharts | null>(null)
  const chartTopTours = useRef<ApexCharts | null>(null)

  const tnd = () => intl.formatMessage({id: 'REPORTS.CURRENCY_TND'})

  // Revenue donut chart
  useEffect(() => {
    chartRevenue.current?.destroy()
    chartRevenue.current = null
    if (!data || !revenueRef.current) return
    const hotelAmt = Number(data.hotelReservations?.totalAmount ?? 0)
    const tourAmt = Number(data.tourReservations?.totalAmount ?? 0)
    if (hotelAmt + tourAmt === 0) return
    const opts: ApexOptions = {
      chart: {type: 'donut', fontFamily: 'Inter, sans-serif', height: 320},
      labels: [
        intl.formatMessage({id: 'REPORTS.HOTEL_RES'}),
        intl.formatMessage({id: 'REPORTS.TOUR_RES'}),
      ],
      series: [hotelAmt, tourAmt],
      colors: [AZ.primary, AZ.secondary],
      legend: {position: 'bottom', fontSize: '13px', labels: {colors: AZ.onSurfaceVariant}},
      plotOptions: {
        pie: {
          donut: {
            size: '72%',
            labels: {
              show: true,
              total: {
                show: true,
                label: intl.formatMessage({id: 'REPORTS.KPI_AMOUNT'}),
                color: AZ.onSurfaceVariant,
                fontSize: '12px',
                formatter: () =>
                  `${new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(
                    hotelAmt + tourAmt,
                  )} ${tnd()}`,
              },
            },
          },
        },
      },
      dataLabels: {enabled: false},
      stroke: {width: 0},
      tooltip: {
        y: {
          formatter: (val: number) =>
            `${new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(val)} ${tnd()}`,
        },
      },
    }
    chartRevenue.current = new ApexCharts(revenueRef.current, opts)
    chartRevenue.current.render()
    return () => {
      chartRevenue.current?.destroy()
      chartRevenue.current = null
    }
  }, [data, intl, from, to])

  // Daily area chart
  useEffect(() => {
    chartDaily.current?.destroy()
    chartDaily.current = null
    if (!data?.charts?.dailyReservations?.length || !dailyRef.current) return
    const rows = data.charts.dailyReservations as {date: string; hotel: number; tour: number}[]
    const opts: ApexOptions = {
      chart: {
        type: 'area',
        fontFamily: 'Inter, sans-serif',
        height: 300,
        toolbar: {show: false},
        zoom: {enabled: false},
      },
      series: [
        {name: intl.formatMessage({id: 'REPORTS.SERIES_HOTEL'}), data: rows.map((r) => r.hotel)},
        {name: intl.formatMessage({id: 'REPORTS.SERIES_TOUR'}), data: rows.map((r) => r.tour)},
      ],
      colors: [AZ.primary, AZ.secondary],
      stroke: {curve: 'smooth', width: 2},
      fill: {type: 'gradient', gradient: {opacityFrom: 0.25, opacityTo: 0.02}},
      xaxis: {
        categories: rows.map((r) => r.date),
        labels: {style: {colors: AZ.outline, fontSize: '11px'}, rotate: -45},
        axisBorder: {show: false},
        axisTicks: {show: false},
      },
      yaxis: {
        labels: {style: {colors: AZ.outline, fontSize: '12px'}},
        title: {text: intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'}), style: {color: AZ.outline}},
      },
      dataLabels: {enabled: false},
      legend: {position: 'top', labels: {colors: AZ.onSurfaceVariant}},
      grid: {borderColor: AZ.surfaceHigh, strokeDashArray: 4},
      tooltip: {shared: true},
    }
    chartDaily.current = new ApexCharts(dailyRef.current, opts)
    chartDaily.current.render()
    return () => {
      chartDaily.current?.destroy()
      chartDaily.current = null
    }
  }, [data, intl, from, to])

  // City bar chart
  useEffect(() => {
    chartCity.current?.destroy()
    chartCity.current = null
    const cities = data?.charts?.reservationsByCity as {cityName: string; count: number}[] | undefined
    if (!cities?.length || !cityRef.current) return
    const opts: ApexOptions = {
      chart: {type: 'bar', height: Math.max(280, cities.length * 28), toolbar: {show: false}},
      series: [{name: intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'}), data: cities.map((c) => c.count)}],
      plotOptions: {bar: {horizontal: true, borderRadius: 6, barHeight: '70%', distributed: true}},
      colors: cities.map((_, i) => (i % 2 === 0 ? AZ.primary : AZ.secondary)),
      xaxis: {
        categories: cities.map((c) => c.cityName || '—'),
        labels: {style: {colors: AZ.outline, fontSize: '11px'}},
        axisBorder: {show: false},
        axisTicks: {show: false},
      },
      yaxis: {labels: {maxWidth: 220, style: {colors: AZ.onSurfaceVariant, fontSize: '11px'}}},
      grid: {borderColor: AZ.surfaceHigh, strokeDashArray: 4},
      dataLabels: {enabled: false},
      legend: {show: false},
    }
    chartCity.current = new ApexCharts(cityRef.current, opts)
    chartCity.current.render()
    return () => {
      chartCity.current?.destroy()
      chartCity.current = null
    }
  }, [data, intl, from, to])

  // Top tours bar chart
  useEffect(() => {
    chartTopTours.current?.destroy()
    chartTopTours.current = null
    const tours = data?.charts?.topTours as {tourId: string; title: string; count: number}[] | undefined
    if (!tours?.length || !topToursRef.current) return
    const opts: ApexOptions = {
      chart: {type: 'bar', height: Math.max(280, tours.length * 32), toolbar: {show: false}},
      series: [{name: intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'}), data: tours.map((t) => t.count)}],
      plotOptions: {bar: {horizontal: true, borderRadius: 6, barHeight: '68%'}},
      colors: [AZ.tertiaryFixed],
      xaxis: {
        categories: tours.map((t) => {
          const max = typeof window !== 'undefined' && window.innerWidth < 992 ? 18 : 42
          return t.title.length > max ? `${t.title.slice(0, max)}…` : t.title
        }),
        labels: {style: {colors: AZ.outline, fontSize: '11px'}},
        axisBorder: {show: false},
        axisTicks: {show: false},
      },
      yaxis: {
        labels: {
          maxWidth: typeof window !== 'undefined' && window.innerWidth < 992 ? 96 : 280,
          style: {colors: AZ.onSurfaceVariant, fontSize: '11px'},
        },
      },
      grid: {borderColor: AZ.surfaceHigh, strokeDashArray: 4},
      dataLabels: {enabled: false},
      legend: {show: false},
    }
    chartTopTours.current = new ApexCharts(topToursRef.current, opts)
    chartTopTours.current.render()
    return () => {
      chartTopTours.current?.destroy()
      chartTopTours.current = null
    }
  }, [data, intl, from, to])

  const fmtMoney = (n: number) =>
    `${new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(n)} ${tnd()}`

  const hotelCount = data?.hotelReservations?.count ?? 0
  const tourCount = data?.tourReservations?.count ?? 0
  const hotelPaidCount = data?.hotelReservations?.paid?.count ?? 0
  const hotelUnpaidCount = data?.hotelReservations?.unpaid?.count ?? 0
  const tourPaidCount = data?.tourReservations?.paid?.count ?? 0
  const tourUnpaidCount = data?.tourReservations?.unpaid?.count ?? 0
  const hotelPaidAmount = Number(data?.hotelReservations?.paid?.totalAmount ?? 0)
  const hotelUnpaidAmount = Number(data?.hotelReservations?.unpaid?.totalAmount ?? 0)
  const tourPaidAmount = Number(data?.tourReservations?.paid?.totalAmount ?? 0)
  const tourUnpaidAmount = Number(data?.tourReservations?.unpaid?.totalAmount ?? 0)
  const totalResCount = data?.reservations?.totalCount ?? hotelCount + tourCount
  const clientsTotal = data?.overview?.clients?.total ?? 0
  const clientsNew = data?.overview?.clients?.newInPeriod ?? 0
  const hotelsTotal = data?.overview?.hotels?.total ?? 0
  const hotelsNew = data?.overview?.hotels?.newInPeriod ?? 0
  const toursPublished = data?.overview?.tours?.published ?? 0
  const toursNew = data?.overview?.tours?.newInPeriod ?? 0
  const charts = data?.charts

  const fieldStyle: React.CSSProperties = {
    backgroundColor: AZ.surfaceLow,
    border: 'none',
    borderRadius: '0.75rem',
    padding: '0.625rem 1rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: AZ.onSurface,
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
  }

  return (
    <div className='reports-page'>

      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div className='reports-page__header'>
        <div>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: '2.25rem',
              fontWeight: 800,
              color: AZ.primary,
              margin: 0,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
            }}
          >
            {intl.formatMessage({id: 'MENU.REPORTS'})}
          </h1>
          <p style={{margin: '0.375rem 0 0', color: AZ.onSurfaceVariant, fontSize: '0.9375rem'}}>
            {intl.formatMessage({id: 'REPORTS.DASHBOARD_SUBTITLE'})}
          </p>
        </div>

        {/* Date filter bar */}
        <div className='reports-page__filters'>
          <div>
            <p style={{margin: '0 0 0.375rem', fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: AZ.onSurfaceVariant}}>
              {intl.formatMessage({id: 'REPORTS.FROM'})}
            </p>
            <input
              id='reports-date-from'
              type='date'
              autoComplete='off'
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={fieldStyle}
              onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px rgba(0,104,118,0.4)')}
              onBlur={(e) => (e.target.style.boxShadow = 'none')}
            />
          </div>
          <div>
            <p style={{margin: '0 0 0.375rem', fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: AZ.onSurfaceVariant}}>
              {intl.formatMessage({id: 'REPORTS.TO'})}
            </p>
            <input
              id='reports-date-to'
              type='date'
              autoComplete='off'
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={fieldStyle}
              onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px rgba(0,104,118,0.4)')}
              onBlur={(e) => (e.target.style.boxShadow = 'none')}
            />
          </div>
          <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
            <button
              type='button'
              onClick={() => refetch()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.625rem 1.25rem',
                background: `linear-gradient(135deg, ${AZ.primary} 0%, ${AZ.primaryContainer} 100%)`,
                color: '#ffffff',
                border: 'none',
                borderRadius: '0.75rem',
                fontWeight: 700,
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <span className='material-symbols-outlined' style={{fontSize: '16px'}}>refresh</span>
              {intl.formatMessage({id: 'REPORTS.REFRESH'})}
            </button>
            <button
              type='button'
              onClick={async () => {
                const res = await dataAxios.get('/reports/financial/export', {params: {from, to}})
                const raw = res.data?.data?.url as string | undefined
                if (raw) {
                  const apiBase = (dataAxios.defaults.baseURL || '').replace(/\/api\/?$/i, '')
                  const rel = raw.replace(/^\.\//, '')
                  window.open(`${apiBase}/${rel}`, '_blank', 'noopener,noreferrer')
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.625rem 1.25rem',
                backgroundColor: AZ.surfaceLow,
                color: AZ.primary,
                border: 'none',
                borderRadius: '0.75rem',
                fontWeight: 700,
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <span className='material-symbols-outlined' style={{fontSize: '16px'}}>download</span>
              {intl.formatMessage({id: 'REPORTS.EXPORT_EXCEL'})}
            </button>
            {isFetching && (
              <span
                className='spinner-border spinner-border-sm'
                role='status'
                style={{color: AZ.primary, width: '1.25rem', height: '1.25rem'}}
              />
            )}
          </div>
        </div>
      </div>

      {/* Loading skeleton */}
      {isLoading && !data && (
        <div style={{display: 'flex', justifyContent: 'center', padding: '4rem 0'}}>
          <span className='spinner-border' role='status' style={{color: AZ.primary, width: '2.5rem', height: '2.5rem'}} />
        </div>
      )}

      {/* ── KPIs: Bookings ────────────────────────────────────────────── */}
      <div>
        <SectionLabel>{intl.formatMessage({id: 'REPORTS.SECTION_BOOKINGS'})}</SectionLabel>
        <div className='reports-kpi-grid reports-kpi-grid--3'>
          <KpiCard
            icon='hotel'
            iconBg='rgba(0,52,127,0.1)'
            iconColor={AZ.primary}
            label={intl.formatMessage({id: 'REPORTS.HOTEL_RES'})}
            value={hotelCount}
            sub={intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'})}
          />
          <KpiCard
            icon='explore'
            iconBg='rgba(0,104,118,0.1)'
            iconColor={AZ.secondary}
            label={intl.formatMessage({id: 'REPORTS.TOUR_RES'})}
            value={tourCount}
            sub={intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'})}
          />
          <KpiCard
            icon='confirmation_number'
            iconBg='rgba(255,184,112,0.15)'
            iconColor='#693c00'
            label={intl.formatMessage({id: 'REPORTS.KPI_TOTAL_RES'})}
            value={totalResCount}
            sub={intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'})}
          />
        </div>
      </div>

      {/* ── KPIs: Payment status ─────────────────────────────────────── */}
      <div>
        <SectionLabel>{intl.formatMessage({id: 'REPORTS.SECTION_PAYMENT_STATUS'})}</SectionLabel>
        <div className='reports-kpi-grid reports-kpi-grid--4'>
          <KpiCard
            icon='paid'
            iconBg='rgba(0,104,118,0.12)'
            iconColor={AZ.secondary}
            label={intl.formatMessage({id: 'REPORTS.HOTEL_RES_PAID'})}
            value={hotelPaidCount}
            sub={fmtMoney(hotelPaidAmount)}
          />
          <KpiCard
            icon='pending_actions'
            iconBg='rgba(255,184,112,0.15)'
            iconColor='#693c00'
            label={intl.formatMessage({id: 'REPORTS.HOTEL_RES_UNPAID'})}
            value={hotelUnpaidCount}
            sub={fmtMoney(hotelUnpaidAmount)}
          />
          <KpiCard
            icon='paid'
            iconBg='rgba(0,52,127,0.1)'
            iconColor={AZ.primary}
            label={intl.formatMessage({id: 'REPORTS.TOUR_RES_PAID'})}
            value={tourPaidCount}
            sub={fmtMoney(tourPaidAmount)}
          />
          <KpiCard
            icon='pending_actions'
            iconBg='rgba(255,184,112,0.15)'
            iconColor='#693c00'
            label={intl.formatMessage({id: 'REPORTS.TOUR_RES_UNPAID'})}
            value={tourUnpaidCount}
            sub={fmtMoney(tourUnpaidAmount)}
          />
        </div>
      </div>

      {/* ── KPIs: Catalog ─────────────────────────────────────────────── */}
      <div>
        <SectionLabel>{intl.formatMessage({id: 'REPORTS.SECTION_CATALOG'})}</SectionLabel>
        <div className='reports-kpi-grid reports-kpi-grid--4'>
          <KpiCard
            icon='groups'
            iconBg='rgba(0,52,127,0.1)'
            iconColor={AZ.primary}
            label={intl.formatMessage({id: 'REPORTS.CLIENTS_TOTAL'})}
            value={clientsTotal}
          />
          <KpiCard
            icon='person_add'
            iconBg='rgba(0,104,118,0.1)'
            iconColor={AZ.secondary}
            label={intl.formatMessage({id: 'REPORTS.CLIENTS_NEW'})}
            value={clientsNew}
          />
          <KpiCard
            icon='apartment'
            iconBg='rgba(0,52,127,0.08)'
            iconColor={AZ.primaryContainer}
            label={intl.formatMessage({id: 'REPORTS.HOTELS_TOTAL'})}
            value={hotelsTotal}
            sub={`${intl.formatMessage({id: 'REPORTS.HOTELS_NEW'})}: ${hotelsNew}`}
          />
          <KpiCard
            icon='map'
            iconBg='rgba(255,184,112,0.15)'
            iconColor='#693c00'
            label={intl.formatMessage({id: 'REPORTS.TOURS_PUBLISHED'})}
            value={toursPublished}
            sub={`${intl.formatMessage({id: 'REPORTS.TOURS_NEW'})}: ${toursNew}`}
          />
        </div>
      </div>

      {/* ── Charts ────────────────────────────────────────────────────── */}
      {data && (
        <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>

          {/* Revenue donut (left 3/5) + breakdown cards (right 2/5) */}
          <div className='reports-charts-split'>
            <ChartCard title={intl.formatMessage({id: 'REPORTS.CHART_REVENUE_TITLE'})}>
              {Number(data.hotelReservations?.totalAmount ?? 0) + Number(data.tourReservations?.totalAmount ?? 0) === 0 ? (
                <p style={{textAlign: 'center', color: AZ.outline, padding: '3rem 0', margin: 0}}>
                  {intl.formatMessage({id: 'REPORTS.NO_DATA'})}
                </p>
              ) : (
                <div ref={revenueRef} />
              )}
            </ChartCard>

            {/* Revenue breakdown — fixed-height tonal cards */}
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {/* Hotel revenue: gradient dark card */}
              <div
                style={{
                  borderRadius: '1.25rem',
                  padding: '1.5rem',
                  background: `linear-gradient(135deg, ${AZ.primary} 0%, ${AZ.primaryContainer} 100%)`,
                  color: '#ffffff',
                }}
              >
                <p style={{margin: 0, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.7}}>
                  {intl.formatMessage({id: 'REPORTS.HOTEL_RES'})}
                </p>
                <p style={{margin: '0.625rem 0 0', fontSize: '1.625rem', fontWeight: 800, fontFamily: 'Manrope, sans-serif', lineHeight: 1, wordBreak: 'break-word'}}>
                  {fmtMoney(Number(data.hotelReservations?.totalAmount ?? 0))}
                </p>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.875rem', opacity: 0.8}}>
                  <span className='material-symbols-outlined' style={{fontSize: '16px', fontVariationSettings: "'FILL' 1"}}>hotel</span>
                  <span style={{fontSize: '0.8125rem'}}>{hotelCount} {intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'})}</span>
                </div>
              </div>

              {/* Tour revenue: white card with teal accent */}
              <div
                style={{
                  borderRadius: '1.25rem',
                  padding: '1.5rem',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 24px -4px rgba(0,52,127,0.07)',
                  borderLeft: `4px solid ${AZ.secondary}`,
                }}
              >
                <p style={{margin: 0, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: AZ.onSurfaceVariant}}>
                  {intl.formatMessage({id: 'REPORTS.TOUR_RES'})}
                </p>
                <p style={{margin: '0.625rem 0 0', fontSize: '1.625rem', fontWeight: 800, fontFamily: 'Manrope, sans-serif', color: AZ.secondary, lineHeight: 1, wordBreak: 'break-word'}}>
                  {fmtMoney(Number(data.tourReservations?.totalAmount ?? 0))}
                </p>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.875rem'}}>
                  <span className='material-symbols-outlined' style={{fontSize: '16px', color: AZ.secondary, fontVariationSettings: "'FILL' 1"}}>explore</span>
                  <span style={{fontSize: '0.8125rem', color: AZ.outline}}>{tourCount} {intl.formatMessage({id: 'REPORTS.KPI_BOOKINGS'})}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Daily trends — full width */}
          <ChartCard title={intl.formatMessage({id: 'REPORTS.CHART_DAILY_TITLE'})}>
            {!charts?.dailyReservations?.length ? (
              <p style={{textAlign: 'center', color: AZ.outline, padding: '3rem 0', margin: 0}}>
                {intl.formatMessage({id: 'REPORTS.NO_DATA'})}
              </p>
            ) : (
              <div ref={dailyRef} />
            )}
          </ChartCard>

          {/* City + Top Tours */}
          <div className='reports-charts-pair'>
            <ChartCard title={intl.formatMessage({id: 'REPORTS.CHART_CITY_TITLE'})}>
              {!charts?.reservationsByCity?.length ? (
                <p style={{textAlign: 'center', color: AZ.outline, padding: '3rem 0', margin: 0}}>
                  {intl.formatMessage({id: 'REPORTS.NO_DATA'})}
                </p>
              ) : (
                <div ref={cityRef} />
              )}
            </ChartCard>
            <ChartCard title={intl.formatMessage({id: 'REPORTS.CHART_TOP_TOURS_TITLE'})}>
              {!charts?.topTours?.length ? (
                <p style={{textAlign: 'center', color: AZ.outline, padding: '3rem 0', margin: 0}}>
                  {intl.formatMessage({id: 'REPORTS.NO_DATA'})}
                </p>
              ) : (
                <div ref={topToursRef} />
              )}
            </ChartCard>
          </div>

          {promoData?.summary && (
            <div style={{marginTop: '2rem'}}>
              <SectionLabel>
                {intl.formatMessage({
                  id: 'REPORTS.PROMOTIONS_TITLE',
                  defaultMessage: 'Promotions & remises',
                })}
              </SectionLabel>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                }}
              >
                <KpiCard
                  icon='sell'
                  iconBg='rgba(0,104,118,0.1)'
                  iconColor={AZ.secondary}
                  label={intl.formatMessage({
                    id: 'REPORTS.PROMO_RESERVATIONS',
                    defaultMessage: 'Réservations avec promo',
                  })}
                  value={promoData.summary.reservationsWithPromo}
                />
                <KpiCard
                  icon='percent'
                  iconBg='rgba(0,52,127,0.08)'
                  iconColor={AZ.primary}
                  label={intl.formatMessage({
                    id: 'REPORTS.PROMO_DISCOUNT_TOTAL',
                    defaultMessage: 'Remises promo',
                  })}
                  value={Number(promoData.summary.totalPromoDiscount).toFixed(2)}
                />
                <KpiCard
                  icon='groups'
                  iconBg='rgba(255,184,112,0.2)'
                  iconColor='#b45309'
                  label={intl.formatMessage({
                    id: 'REPORTS.GROUP_DISCOUNT_TOTAL',
                    defaultMessage: 'Remises groupe',
                  })}
                  value={Number(promoData.summary.totalGroupDiscount).toFixed(2)}
                />
              </div>
              {promoData.topPromotions?.length > 0 && (
                <ChartCard
                  title={intl.formatMessage({
                    id: 'REPORTS.TOP_PROMO_CODES',
                    defaultMessage: 'Top codes promo',
                  })}
                >
                  <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle gs-0 gy-3' style={{minWidth: 420}}>
                    <thead>
                      <tr className='text-muted fw-bold fs-7 text-uppercase'>
                        <th>Code</th>
                        <th>{intl.formatMessage({id: 'TOUR.PROMOTIONS.USAGE', defaultMessage: 'Utilisations'})}</th>
                        <th>{intl.formatMessage({id: 'TOUR.PROMOTIONS.VALUE', defaultMessage: 'Valeur'})}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoData.topPromotions.map((p: {code: string; usages: number; discountTotal: number}) => (
                        <tr key={p.code}>
                          <td>
                            <span className='badge badge-light-primary'>{p.code}</span>
                          </td>
                          <td>{p.usages}</td>
                          <td>{Number(p.discountTotal).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </ChartCard>
              )}
            </div>
          )}

        </div>
      )}

      {/* Currency hint */}
      {data && (
        <p style={{fontSize: '0.75rem', color: AZ.outline, margin: 0}}>
          {intl.formatMessage({id: 'REPORTS.CURRENCY_HINT'})}
        </p>
      )}
    </div>
  )
}

export default ReportsPage
